import {
  useState, useEffect, forwardRef, useImperativeHandle, useReducer, useRef,
} from 'react';
import { nanoid } from 'nanoid';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import Icon from '@/core/icon.js';
import { useTranslation } from '@/helpers/translate';
import { usePatternAutocomplete } from '@/helpers/usePatternAutocomplete';

function MultiValueRemove(props) {
  return <components.MultiValueRemove {...props}>
    <Icon name="close" className="fill-current size-1.5" />
         </components.MultiValueRemove>;
}

function ClearIndicator(props) {
  return <components.ClearIndicator {...props}>
    <Icon name="close" className="fill-current size-3 text-purple-5/50 hover:text-purple" />
         </components.ClearIndicator>;
}

const selectClassNames = {
  control: (state) => (state.isFocused
    ? '!border-purple !shadow-none !ring-2 !ring-purple/25'
    : '!border-purple-4/40 px-2'),
  option: (state) => (state.isFocused
    ? '!bg-purple/10 !text-purple !cursor-pointer !text-sm'
    : '!bg-transparent !text-purple-6 !cursor-pointer hover:!bg-purple/10 hover:!text-purple !text-sm'),
  multiValue: () => '!bg-purple/10 !rounded',
  multiValueLabel: () => '!text-purple !text-sm',
  multiValueRemove: () => '!text-purple hover:!bg-purple/20 hover:!text-purple',
  placeholder: () => '!text-purple-5/50',
  menu: () => '!border !border-purple/20 !shadow-lg',
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#7C3AED' : '#a78bfa66',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(124, 58, 237, 0.25)' : 'none',
    '&:hover': {
      borderColor: '#7C3AED',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'rgba(124, 58, 237, 0.1)' : 'transparent',
    color: state.isFocused ? '#7C3AED' : '#1e1b4b',
    fontSize: '0.875rem',
    '&:hover': {
      backgroundColor: 'rgba(124, 58, 237, 0.1)',
      color: '#7C3AED',
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'rgba(124, 58, 237, 0.1)',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#7C3AED',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#7C3AED',
    '&:hover': {
      backgroundColor: 'rgba(124, 58, 237, 0.2)',
      color: '#7C3AED',
    },
  }),
};

const ByPattern = forwardRef(({
  item, onScreen, onFilter, onValid, renderButtonStartTour,
}, ref) => {
  const { t } = useTranslation();
  const inputRef = useRef();
  const [keySlug] = useState(item.id);
  const { loadOptions: dbLoadOptions } = usePatternAutocomplete();

  const modalRef = useRef(null);
  const [alignRight, setAlignRight] = useState(false);

  function extractDataFromUrl(values) {
    // console.log('extractDataFromUrl', values);
    const valueSplit = (values || '').split(',');
    if (item?.defaultOptions) {
      const valueSplitLower = valueSplit.map((valueSss) => valueSss.toLowerCase());
      return item?.defaultOptions.filter((o) => valueSplitLower.includes(o.label.toLowerCase()));
    }

    return valueSplit.filter((vvv) => !!vvv).map((val) => ({ value: val, label: val }));
  }

  const [currentValue, setCurrentValue] = useState({
    validValue: extractDataFromUrl(useSearchZustandStore.getState()?.initParams?.[item.slug]),
    isChange: null,
  });

  const [selectedOptions, setSelectedOptions] = useState(currentValue?.validValue || []);

  const [isOpenModal, setIsOpenModal] = useReducer((prev, newValue) => {
    let tmpValue = newValue;
    if (!onScreen) return prev;

    if (tmpValue === 'finalclose') {
      tmpValue = false;
    } else if (tmpValue === 'iconclose' || tmpValue === 'outside') {
      // eslint-disable-line no-empty
    } else if (!tmpValue) {
      if (onValid) tmpValue = 'willCloseAndValid';
    }
    return tmpValue;
  }, false);

  function getValue() {
    // console.log('selectedOptions', selectedOptions)
    if (!selectedOptions?.length) return null;
    return (selectedOptions || []).map(({ value }) => value).filter((vvv) => !!vvv);
    // return currentValue?.validValue;
  }

  function getOutput() {
    return {
      keySlug,
      slug: item.slug,
      url: getValue(),
      values: getValue(),
      graphql: {
        [item.slug]: getValue(),
      },
    };
  }

  function clearValue(runSearch, e) {
    if (e) e.stopPropagation();
    setCurrentValue({ isChange: runSearch ? nanoid() : null, validValue: null });
    setSelectedOptions([]);
    setIsOpenModal(false);
  }

  function isSameDefault() {
    return !currentValue?.validValue?.length;
  }

  const adjustModal = () => {
    console.log('modalRefmodalRef', modalRef);
    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect();
      console.log('rectrectrect', rect);
      // Si le bord droit sort de l'écran
      if (rect.right > window.innerWidth) {
        setAlignRight(true);
      } else {
        setAlignRight(false);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    getValue,
    getOutput,
    clearValue,
    setIsOpenModal,
    isSameDefault,
  }), [currentValue]);

  useEffect(() => {
    if (currentValue.isChange) {
      if (onValid) onValid();
    }
    adjustModal();
  }, [currentValue.isChange]);

  useEffect(() => {
    if (isOpenModal === 'willCloseAndValid') {
      onValid();
      setIsOpenModal('finalclose');
    } else if (isOpenModal === 'iconclose' || isOpenModal === 'outside') {
      onValid(isOpenModal);
      setIsOpenModal('finalclose');
    } else if (isOpenModal) {
      inputRef?.current?.focus();
      adjustModal();
      window.addEventListener('resize', adjustModal);
    }

    return () => {
      setAlignRight(false);
      window.removeEventListener('resize', adjustModal);
    };
  }, [isOpenModal]);

  function onClick(newValue) {
    if (newValue === getValue()) return setCurrentValue({ validValue: null, isChange: nanoid() });
    return setCurrentValue({ validValue: newValue, isChange: nanoid() });
  }

  function closeOnlyNotSaveValues(action) {
    setCurrentValue({ value: currentValue.validValue });
    setIsOpenModal(action);
  }

  function callrunSearch() {
    const validValue = selectedOptions;
    setCurrentValue({
      isChange: nanoid(),
      validValue, // inputRef.current.value
    });
    setIsOpenModal(false);
  }

  function onKeyPress(event) {
    if (event.charCode === 13) callrunSearch(event);
  }

  function functionWithDefaultOption(inputValue, callback) {
    const resultat = item?.defaultOptions.filter((o) => o.label.toLowerCase().includes(inputValue.toLowerCase()));
    callback(resultat);
  }

  function getResume() {
    return (selectedOptions || []).map(({ value }) => value).filter((vvv) => !!vvv).join(' - ');
  }
  function renderResume() {
    if (!getValue()) return null;
    const isPattern = item.slug === 'pattern';
    return (
      <p className={`flex items-center transition h-9 px-[8px] rounded-r cursor-pointer max-w-[150px] ${isPattern ? 'bg-red' : 'bg-purple'}`}>
        <span className="mr-2 text-white truncate" onClick={() => setIsOpenModal(true)}>
          {getResume()}
        </span>
        <button className="w-3 shrink-0" onClick={(e) => { clearValue(true, e); }}><Icon name="close" className="w-full text-white fill-current size-1.5" /></button>
      </p>
    );
  }

  function renderTitle() {
    const isPattern = item.slug === 'pattern';
    return (
      <div className={`flex items-center justify-between overflow-hidden text-sm text-white transition-all duration-200 rounded-lg cursor-pointer group ${isPattern ? 'bg-red/70 hover:bg-red' : 'bg-purple-6 hover:bg-purple'}`}>
        <p className="truncate flex items-center justify-center w-full text-center py-[8px] px-[8px] min-w-[110px]" onClick={() => setIsOpenModal(true)}>
          {!item?.noicon && <Icon className={`w-5 h-5 mr-2 transition-colors duration-200 ${isPattern ? 'text-white/70' : 'text-purple-4/70'} group-hover:text-white`} name={item.icon || item.slug} />}
          <span className="transition-colors duration-200">{t(item.title)}</span>
          <Icon name="next" className={`w-2 h-2 ml-2 transition-colors duration-200 transform rotate-90 ${isPattern ? 'text-white/50' : 'text-purple-1'} group-hover:text-white`} />
        </p>
        {renderResume()}
      </div>
    );
  }

  function renderContent() {
    return (
      // div et non p : react-select rend des <div> à l'intérieur, ce que HTML interdit dans un <p>
      // ("validateDOMNesting: <div> cannot appear as a descendant of <p>"). Aucun style ne cible
      // p ici (seul p > a existe en global), la boîte est donc identique.
      <div className="flex items-center mt-2 transition">
        <AsyncSelect
          isMulti
          {...(
            item?.defaultOptions ? {
              defaultOptions: item?.defaultOptions,
              loadOptions: functionWithDefaultOption,
            } : {
              loadOptions: dbLoadOptions,
            }
          )}
          placeholder={t('search:filter.patterns.placeholder')}
          onChange={setSelectedOptions}
          defaultValue={selectedOptions}
          className="w-full"
          classNames={selectClassNames}
          styles={selectStyles}
          components={{ MultiValueRemove, ClearIndicator }}
        />
      </div>
    );
  }

  function renderModal() {
    return (
      <>
        <div className="absolute top-0 left-0 z-30 w-screen h-screen bg-purple-6/50" onClick={() => { closeOnlyNotSaveValues('outside'); }} />
        <div className="-mt-[38px]">
          <div
            className={`min-w-[450px] rounded-lg bg-white z-40 absolute px-6 pt-6 pb-4 shadow-button
            ${alignRight
              ? 'right-0 left-auto translate-x-0'
              : ''}`}
            ref={modalRef}
          >
          <h1 className="mb-2 font-medium text-purple-6">{t(item.title)}</h1>
            <button
              className="absolute top-4 right-4 size-5"
              onClick={() => {
                closeOnlyNotSaveValues('iconclose');
              }}
            >
              <Icon name="close" className="mx-1 fill-current size-4 text-purple-6/30 hover:text-black" />
            </button>
            {renderContent()}
            <div className="flex justify-between items-center mt-4 mb-2">
              <button className="text-base rounded-none border-b text-purple-5/50 border-purple-5/50 hover:text-purple hover:border-purple" onClick={(e) => clearValue(true, e)}>{t('search:clean')}</button>
              <div>
                {renderButtonStartTour?.()}
                <button className="px-6 py-2 purple-gradient" onClick={() => callrunSearch()}>{t('search:save')}</button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderFilter() {
    return 'bypattern dont have ux for on filter';
  }

  if (!item.modal) return renderContent();

  return (
    <>
      {!(onScreen && !isOpenModal) || renderTitle()}
      {!(onScreen && isOpenModal) || (
      <>
        {renderTitle()}
        {renderModal()}
      </>
      )}
      {!onFilter || renderFilter()}
    </>
  );
});

export default ByPattern;
